/**
 * Paid options added as products (custom), so that the ERP gets them as order lines. Texts are stored in line item
 * properties.
 *
 * - <product-addon>: "Add-on product" block of the product page (e.g. birthday plaque on pastries): checkbox and optional
 *   text field, tied to the product form by its "form" attribute. When checked, the form submission is taken over
 *   (capture listener, so it runs before the theme's product-form handler) to add the product and the add-on in a single
 *   request, the add-on nested under the product line. It then dispatches the same events as the theme, so the cart
 *   drawer behaves as usual.
 * - <cart-message>: order level option below the cart line items (checkbox + text field). The message is added once, as a
 *   regular line (not nested), which is not listed with the other line items. Unchecking the box removes it. It is only
 *   offered for postal shipping: see `CartMessage`.
 */

const cartRoute = (path) => `${window.Shopify?.routes?.root ?? '/'}${path}`;

const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';

/** Asks the theme sections (cart drawer, cart delivery alert…) which sections to render along with the request. */
const getBundledSections = () => {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
  return sections;
};

const setLoading = (isLoading) => {
  document.documentElement.dispatchEvent(new CustomEvent(isLoading ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true }));
};

/** Same as the theme's cart requests: the full cart, with the rendered sections of the response attached. */
const getCartWithSections = async (sections) => {
  const cart = await (await fetch(cartRoute('cart.js'))).json();
  cart.sections = sections;
  return cart;
};

/* ---------------------------------------------------------------------------------------------------------------------
 * Product form
 * ------------------------------------------------------------------------------------------------------------------- */

class ProductAddon extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.checkbox?.addEventListener('change', () => this.#onToggle({ focus: true }), { signal: this.#abortController.signal });
    this.#onToggle();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get checkbox() {
    return this.querySelector('input[type="checkbox"]');
  }

  /* Optional: the text field is a setting of the block */
  get textarea() {
    return this.querySelector('textarea');
  }

  get isActive() {
    return Boolean(this.checkbox?.checked);
  }

  get text() {
    return this.textarea?.value.trim() ?? '';
  }

  reset() {
    this.checkbox.checked = false;

    if (this.textarea) {
      this.textarea.value = '';
    }

    this.#onToggle();
  }

  #onToggle({ focus = false } = {}) {
    const field = this.querySelector('.product-addon__field');

    if (!field || !this.textarea) {
      return;
    }

    field.hidden = !this.isActive;
    this.textarea.required = this.isActive;

    if (this.isActive && focus) {
      this.textarea.focus();
    }
  }
}

const onProductFormSubmit = async (event) => {
  const form = event.target;

  if (!(form instanceof HTMLFormElement) || !form.closest('product-form')) {
    return;
  }

  // The block can sit anywhere in the product info, outside of the form element. The ID is read as an attribute:
  // `form.id` returns the form's <input name="id"> (the variant), not the form ID.
  const formId = form.getAttribute('id');
  const addon = formId ? document.querySelector(`product-addon[form="${CSS.escape(formId)}"]`) : null;

  if (!addon?.isActive) {
    return; // The theme's product form handles the submission on its own
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  if (event.submitter?.getAttribute('aria-busy') === 'true') {
    return;
  }

  // A text made of spaces only is not a text
  if (addon.textarea) {
    addon.textarea.value = addon.text;
  }

  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }

  const formData = new FormData(form);
  const parentId = Number(formData.get('id'));
  const parentLine = { id: parentId, quantity: Number(formData.get('quantity') || 1) };
  const properties = {};

  for (const [name, value] of formData.entries()) {
    const match = name.match(/^properties\[(.+)\]$/);

    if (match && value !== '') {
      properties[match[1]] = value;
    }
  }

  if (Object.keys(properties).length > 0) {
    parentLine.properties = properties;
  }

  if (formData.get('selling_plan')) {
    parentLine.selling_plan = Number(formData.get('selling_plan'));
  }

  const addonLine = { id: Number(addon.getAttribute('variant-id')), quantity: 1, parent_id: parentId };

  if (addon.textarea) {
    addonLine.properties = { [addon.getAttribute('property-name')]: addon.text };
  }

  const submitButtons = Array.from(form.elements).filter((element) => element.type === 'submit');
  submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
  setLoading(true);

  const sections = getBundledSections();

  try {
    const response = await fetch(cartRoute('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [parentLine, addonLine],
        sections: sections.join(','),
        sections_url: cartRoute(`variants/${parentId}`),
      }),
    });

    const responseJson = await response.json();

    if (!response.ok) {
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson.description } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }

    addon.reset();

    if (window.themeVariables?.settings?.cartType === 'page' || isCartPage()) {
      window.location.href = cartRoute('cart');
      return;
    }

    const cart = await getCartWithSections(responseJson.sections);
    const onSuccessDo = formData.get('on_success');

    form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: responseJson.items, cart, onSuccessDo } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo, cart } }));
  } finally {
    submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
    setLoading(false);
  }
};

/* ---------------------------------------------------------------------------------------------------------------------
 * Cart
 * ------------------------------------------------------------------------------------------------------------------- */

/** Checkout method picked in the Zapiet Pickup + Delivery widget, kept across the reloads of the cart page. */
const CHECKOUT_METHOD_STORAGE_KEY = 'cart-message:checkout-method';

const ZAPIET_READY_TIMEOUT = 5000;

/**
 * Checkout method picked in the Zapiet widget of the cart page ("shipping", "pickup"…), or null when unknown: not
 * chosen yet, or no widget on the page.
 */
const getCheckoutMethod = () => window.Zapiet?.getSelectedCheckoutMethod?.() ?? null;

/** Resolves once the Zapiet widget has rendered its methods, or with false after a while. */
const waitForZapiet = () => {
  const startedAt = Date.now();

  return new Promise((resolve) => {
    const check = () => {
      if (window.Zapiet?.setCheckoutMethod && document.querySelector('#storePickupApp .checkoutMethod')) {
        resolve(true);
      } else if (Date.now() - startedAt > ZAPIET_READY_TIMEOUT) {
        resolve(false);
      } else {
        setTimeout(check, 100);
      }
    };

    check();
  });
};

/**
 * The message is a letter slipped into the parcel, so it is only offered for postal shipping: the block is hidden (by
 * Liquid, then here) until "shipping" is picked in the Zapiet widget, and picking another method (pickup) removes a
 * message already in the cart. While the method is unknown, a message already in the cart stays visible so that it can
 * be edited or removed.
 *
 * Zapiet forgets the picked method when the page reloads, which the cart page does after every change: the method is
 * kept in session storage and picked again on load, so the customer does not have to.
 */
class CartMessage extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.checkbox.addEventListener('change', this.#onToggle, { signal });
    this.saveButton.addEventListener('click', this.#save, { signal });
    this.textarea.addEventListener('input', this.#onInput, { signal });
    document.addEventListener('zapiet:selected_method', this.#onCheckoutMethodChange, { signal });

    // The browser may restore a checked box or a typed text on reload: always start from the actual cart state
    this.checkbox.checked = this.checkbox.defaultChecked;
    this.textarea.value = this.textarea.defaultValue;
    this.fields.hidden = !this.checkbox.checked;

    this.#onCheckoutMethodChange();
    this.#restoreCheckoutMethod();
  }

  /* Zapiet's setCheckoutMethod does not dispatch "selected_method", hence the explicit update */
  async #restoreCheckoutMethod() {
    let method = null;

    try {
      method = sessionStorage.getItem(CHECKOUT_METHOD_STORAGE_KEY);
      sessionStorage.removeItem(CHECKOUT_METHOD_STORAGE_KEY);
    } catch {
      // Storage unavailable: the customer picks the method again
    }

    if (method && getCheckoutMethod() === null && (await waitForZapiet())) {
      window.Zapiet.setCheckoutMethod(method);
      this.#onCheckoutMethodChange();
    }
  }

  /* The event carries the new method, which may not be the selected one yet when it is dispatched */
  #onCheckoutMethodChange = (event) => {
    const method = typeof event?.detail === 'string' ? event.detail : getCheckoutMethod();

    if (method === 'shipping') {
      this.hidden = false;
    } else if (method === null) {
      this.hidden = !this.lineKey;
    } else {
      this.hidden = true;

      if (this.lineKey) {
        this.#request('cart/change.js', { id: this.lineKey, quantity: 0 });
      }
    }
  };

  /* The "added" status only describes the saved text */
  #onInput = () => {
    const status = this.querySelector('.cart-message__status');

    if (status) {
      status.hidden = this.textarea.value.trim() !== this.textarea.defaultValue;
    }
  };

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get checkbox() {
    return this.querySelector('input[type="checkbox"]');
  }

  get fields() {
    return this.querySelector('.cart-message__fields');
  }

  get textarea() {
    return this.querySelector('textarea');
  }

  get saveButton() {
    return this.fields.querySelector('.button');
  }

  get lineKey() {
    return this.getAttribute('line-key');
  }

  #onToggle = () => {
    if (this.checkbox.checked) {
      this.fields.hidden = false;
      this.textarea.focus();
    } else if (this.lineKey) {
      this.#request('cart/change.js', { id: this.lineKey, quantity: 0 });
    } else {
      this.fields.hidden = true;
    }
  };

  #save = () => {
    const message = this.textarea.value.trim();
    this.textarea.value = message;

    // No "required" attribute here: on the cart page the field lives inside the checkout form, which it would block
    if (message === '') {
      this.textarea.focus();
      return;
    }

    const properties = { [this.getAttribute('property-name')]: message };

    if (this.lineKey) {
      this.#request('cart/change.js', { id: this.lineKey, quantity: 1, properties });
    } else {
      this.#request('cart/add.js', { items: [{ id: Number(this.getAttribute('variant-id')), quantity: 1, properties }] });
    }
  };

  async #request(route, body) {
    if (this.getAttribute('aria-busy') === 'true') {
      return;
    }

    this.setAttribute('aria-busy', 'true');
    this.saveButton.setAttribute('aria-busy', 'true');
    setLoading(true);

    const sections = getBundledSections();

    try {
      const response = await fetch(cartRoute(route), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ ...body, sections: sections.join(',') }),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        this.#showError(responseJson.description ?? responseJson.message);
        return;
      }

      if (isCartPage()) {
        try {
          const method = getCheckoutMethod();

          if (method) {
            sessionStorage.setItem(CHECKOUT_METHOD_STORAGE_KEY, method);
          }
        } catch {
          // Storage unavailable: the customer picks the method again
        }

        window.location.reload();
        return;
      }

      const cart = await getCartWithSections(responseJson.sections);
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } }));
    } finally {
      this.removeAttribute('aria-busy');
      this.saveButton.removeAttribute('aria-busy');
      setLoading(false);
    }
  }

  #showError(error) {
    this.querySelector('.cart-message__error')?.remove();
    this.insertAdjacentHTML('beforeend', '<p class="cart-message__error text-xs" role="alert"></p>');
    this.querySelector('.cart-message__error').textContent = error;
  }
}

if (!window.customElements.get('product-addon')) {
  window.customElements.define('product-addon', ProductAddon);
  document.addEventListener('submit', onProductFormSubmit, { capture: true });
}

if (!window.customElements.get('cart-message')) {
  window.customElements.define('cart-message', CartMessage);
}
